import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search, Users, Mail, Phone, FileText, Euro, Star } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";

export default function AdminClients() {
  const [clients, setClients] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/admin/clients").then(({ data }) => setClients(data)).catch(() => setClients([]));
  }, []);

  const togglePriority = async (c) => {
    try {
      await api.post(`/admin/clients/${c.id}/priority`, { priority: !c.priority });
      setClients((prev) => prev.map((x) => (x.id === c.id ? { ...x, priority: !c.priority } : x)));
      toast.success(!c.priority ? `${c.name} défini comme prioritaire ⭐` : "Priorité retirée");
    } catch { toast.error("Action impossible"); }
  };

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    let list = clients || [];
    if (s) list = list.filter((c) => [c.name, c.email, c.phone].filter(Boolean).some((f) => String(f).toLowerCase().includes(s)));
    // priority clients first
    return [...list].sort((a, b) => (b.priority ? 1 : 0) - (a.priority ? 1 : 0));
  }, [clients, search]);

  return (
    <div>
      <h1 className="font-display text-3xl font-bold tracking-tight">Clients</h1>
      <p className="mt-1.5 text-sm text-slate-500">{clients?.length || 0} client{(clients?.length || 0) > 1 ? "s" : ""} inscrit{(clients?.length || 0) > 1 ? "s" : ""}. Marquez vos clients VIP comme prioritaires ⭐.</p>

      <div className="relative mt-6 max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="clients-search" placeholder="Rechercher un client..." className="pl-9" />
      </div>

      <div className="mt-6">
        {clients === null ? (
          <div className="py-16 text-center text-slate-400">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center">
            <Users className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 text-sm text-slate-500">Aucun client.</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="clients-list">
            {filtered.map((c) => (
              <div key={c.id} className={`rounded-xl border bg-white p-5 ${c.priority ? "border-amber-300 ring-1 ring-amber-200" : "border-slate-200"}`} data-testid={`client-${c.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-3">
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-blue-100 font-display text-lg font-semibold text-blue-700">{c.name?.charAt(0)?.toUpperCase()}</span>
                    <div className="min-w-0">
                      <p className="truncate font-display font-semibold text-slate-900">{c.name}</p>
                      <div className="flex items-center gap-1.5">
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">{c.provider === "google" ? "Google" : "Email"}</span>
                        {c.priority && <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-700"><Star className="h-2.5 w-2.5 fill-amber-500" /> VIP</span>}
                      </div>
                    </div>
                  </div>
                  <button onClick={() => togglePriority(c)} data-testid={`toggle-priority-${c.id}`}
                    title={c.priority ? "Retirer la priorité" : "Définir prioritaire"}
                    className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg transition-colors ${c.priority ? "bg-amber-100 text-amber-600 hover:bg-amber-200" : "text-slate-300 hover:bg-slate-100 hover:text-amber-500"}`}>
                    <Star className={`h-5 w-5 ${c.priority ? "fill-amber-500" : ""}`} />
                  </button>
                </div>
                <div className="mt-4 space-y-1.5 text-sm text-slate-500">
                  <p className="flex items-center gap-2 truncate"><Mail className="h-3.5 w-3.5 shrink-0" /> {c.email}</p>
                  {c.phone && <p className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" /> {c.phone}</p>}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2 border-t border-slate-100 pt-3 text-sm">
                  <div className="flex items-center gap-1.5 text-slate-600"><FileText className="h-4 w-4 text-blue-500" /> {c.requests} demande{c.requests > 1 ? "s" : ""}</div>
                  <div className="flex items-center justify-end gap-1.5 font-semibold text-emerald-600"><Euro className="h-4 w-4" /> {c.revenue.toFixed(2)} €</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
